import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  getSkillTest,
  openSkillTest,
  submitSkillTestAnswer,
  type SkillTestRequestError,
} from '../../auth/_requests';
import { Spinner } from '../../helpers/UI/Spinner/Spinner';
import { InlineAlert } from '../../helpers/UI/InlineAlert/InlineAlert';
import logoUrl from '../../assets/helpers/navbar/tenantpay-white.png';
import { ContestWinnerStatusEnum, fadeInUpAnimation, type SkillTestData } from '../../helpers/utils/types';
import { AuthPageShell } from '../Login/AuthPageShell';
import loginStyles from '../Login/Login.module.css';
import { QuestionCard } from './components/QuestionCard';
import { RulesConfirmation } from './components/RulesConfirmation';
import { ConfettiCelebration } from './components/ConfettiCelebration';
import { TerminalState } from './components/TerminalState';
import {
  computeRemainingSecondsFromDeadline,
  getAttemptsRemaining,
  isDeadlinePassedError,
  isSkillTestActive,
  isSkillTestNotOpenedError,
  mapForbiddenToTerminal,
  resolveInitialTerminalVariant,
  type SkillTestTerminalVariant,
} from './skillTestUtils';

type ScreenPhase = 'loading' | 'load_error' | 'confirmation' | 'question' | 'terminal';

function parseAnswerInput(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

export default function SkillTestPage() {
  const { token: pathToken } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? pathToken ?? undefined;
  const [phase, setPhase] = useState<ScreenPhase>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [terminalVariant, setTerminalVariant] = useState<SkillTestTerminalVariant | null>(null);

  const [session, setSession] = useState<SkillTestData | null>(null);
  const [answerInput, setAnswerInput] = useState('');
  const [attestationConfirmed, setAttestationConfirmed] = useState(false);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const isOpeningRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const timerIntervalRef = useRef<number | null>(null);
  const hasTerminalTransitionRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerIntervalRef.current !== null) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const goToTerminal = useCallback(
    (variant: SkillTestTerminalVariant) => {
      if (hasTerminalTransitionRef.current) {
        return;
      }

      hasTerminalTransitionRef.current = true;
      clearTimer();
      setTerminalVariant(variant);
      setPhase('terminal');
      setIsLocked(true);
    },
    [clearTimer]
  );

  const handleTimerExpired = useCallback(() => {
    goToTerminal('expired');
  }, [goToTerminal]);

  const startTimer = useCallback(
    (answerDeadline: string) => {
      clearTimer();

      const tick = () => {
        const remaining = computeRemainingSecondsFromDeadline(answerDeadline);
        setSecondsRemaining(remaining);

        if (remaining <= 0) {
          clearTimer();
          handleTimerExpired();
        }
      };

      tick();
      timerIntervalRef.current = window.setInterval(tick, 250);
    },
    [clearTimer, handleTimerExpired]
  );

  const applySessionData = useCallback(
    (data: SkillTestData) => {
      setSession(data);

      const terminal = resolveInitialTerminalVariant(data);
      if (terminal) {
        goToTerminal(terminal);
        return;
      }

      if (data.status === ContestWinnerStatusEnum.Pending && !data.openedAt) {
        setPhase('confirmation');
        return;
      }

      if (isSkillTestActive(data) && data.answerDeadline) {
        setPhase('question');
        startTimer(data.answerDeadline);
      }
    },
    [goToTerminal, startTimer]
  );

  const fetchSession = useCallback(async () => {
    if (!token) {
      goToTerminal('invalid_token');
      return;
    }

    setPhase('loading');
    setLoadError(null);

    try {
      const data = await getSkillTest(token);
      applySessionData(data);
    } catch (error) {
      const requestError = error as SkillTestRequestError;

      if (requestError.statusCode === 401 || requestError.statusCode === 404) {
        goToTerminal('invalid_token');
        return;
      }

      if (requestError.statusCode === 403) {
        goToTerminal(mapForbiddenToTerminal(requestError.message));
        return;
      }

      setLoadError(requestError.message || 'Unable to load the skill test. Please try again.');
      setPhase('load_error');
    }
  }, [token, applySessionData, goToTerminal]);

  useEffect(() => {
    fetchSession();

    return () => {
      clearTimer();
    };
  }, [fetchSession, clearTimer]);

  const handleStart = async () => {
    if (!token || isOpeningRef.current || isStarting) {
      return;
    }

    isOpeningRef.current = true;
    setIsStarting(true);
    setStartError(null);
    setRetryMessage(null);
    setSubmitError(null);
    setAttestationConfirmed(false);

    try {
      const data = await openSkillTest(token);
      applySessionData(data);
    } catch (error) {
      const requestError = error as SkillTestRequestError;

      if (requestError.statusCode === 403) {
        goToTerminal(mapForbiddenToTerminal(requestError.message));
        return;
      }

      if (requestError.statusCode === 401 || requestError.statusCode === 404) {
        goToTerminal('invalid_token');
        return;
      }

      setStartError(requestError.message || 'Unable to start the skill test. Please try again.');
      setPhase('confirmation');
    } finally {
      isOpeningRef.current = false;
      setIsStarting(false);
    }
  };

  const handleSubmit = async () => {
    if (!token || isLocked || isSubmittingRef.current || isSubmitting) {
      return;
    }

    const parsedAnswer = parseAnswerInput(answerInput);
    if (parsedAnswer === null) {
      setSubmitError('Please enter a valid whole number greater than zero.');
      return;
    }

    if (!attestationConfirmed) {
      setSubmitError('Please confirm you answered this question yourself before submitting.');
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitSkillTestAnswer(token, parsedAnswer);
      setSession(result);

      if (result.status === ContestWinnerStatusEnum.Awarded) {
        goToTerminal('success');
        return;
      }

      if (result.status === ContestWinnerStatusEnum.Forfeited) {
        goToTerminal('failed');
        return;
      }

      if (result.status === ContestWinnerStatusEnum.Pending) {
        const attemptsRemaining = getAttemptsRemaining(result.attemptCount);

        if (attemptsRemaining <= 0) {
          goToTerminal('failed');
          return;
        }

        setRetryMessage(`That's not quite right, ${attemptsRemaining} attempt(s) left.`);
        setAnswerInput('');
        setAttestationConfirmed(false);
      }
    } catch (error) {
      const requestError = error as SkillTestRequestError;

      if (requestError.statusCode === 400) {
        if (isDeadlinePassedError(requestError.message)) {
          goToTerminal('expired');
          return;
        }

        if (isSkillTestNotOpenedError(requestError.message)) {
          setPhase('confirmation');
          setSubmitError('Please start the skill test before submitting an answer.');
          return;
        }
      }

      if (requestError.statusCode === 403) {
        goToTerminal(mapForbiddenToTerminal(requestError.message));
        return;
      }

      setSubmitError(requestError.message || 'Something went wrong. Please try again.');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (phase === 'loading') {
      return (
        <div className='flex min-h-[280px] items-center justify-center'>
          <Spinner />
        </div>
      );
    }

    if (phase === 'load_error') {
      return (
        <div className='space-y-4'>
          <InlineAlert type='error' title='Unable to load skill test' message={loadError ?? ''} />
          <button
            type='button'
            onClick={fetchSession}
            className='w-full rounded-[10px] bg-[#0055FF] px-4 py-3 text-[14px] font-semibold text-white transition hover:bg-[#0046D5]'
          >
            Try again
          </button>
        </div>
      );
    }

    if (phase === 'terminal' && terminalVariant) {
      return <TerminalState variant={terminalVariant} />;
    }

    if (phase === 'confirmation' && session) {
      return (
        <div className='space-y-4'>
          {startError && (
            <InlineAlert
              type='error'
              title='Unable to start'
              message={startError}
              dismissible
              onDismiss={() => setStartError(null)}
            />
          )}
          <RulesConfirmation onStart={handleStart} isStarting={isStarting} />
        </div>
      );
    }

    if (phase === 'question' && session) {
      return (
        <QuestionCard
          question={session.question}
          answerInput={answerInput}
          onAnswerChange={setAnswerInput}
          attestationConfirmed={attestationConfirmed}
          onAttestationChange={setAttestationConfirmed}
          onSubmit={handleSubmit}
          secondsRemaining={secondsRemaining}
          attemptCount={session.attemptCount}
          retryMessage={retryMessage}
          submitError={submitError}
          onDismissSubmitError={() => setSubmitError(null)}
          isSubmitting={isSubmitting}
          isLocked={isLocked}
        />
      );
    }

    return null;
  };

  const showSuccessCelebration = phase === 'terminal' && terminalVariant === 'success';

  return (
    <AuthPageShell variant='center'>
      {showSuccessCelebration && <ConfettiCelebration />}
      <div className={`${loginStyles.loginCardShell} ${loginStyles.loginCardShellSkillTest}`}>
        <motion.main
          className={`${loginStyles.main} ${loginStyles.mainSkillTest}`}
          {...fadeInUpAnimation}
          initial={fadeInUpAnimation.initial}
          animate={fadeInUpAnimation.animate}
          transition={fadeInUpAnimation.transition}
        >
          <div className={loginStyles.logoWrap}>
            <img src={logoUrl} className={loginStyles.logo} alt='logo' decoding='async' />
          </div>
          {renderContent()}
        </motion.main>
      </div>
    </AuthPageShell>
  );
}
